import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import { createDayjs } from '@chiroup/core/functions/time';
import { Dayjs } from 'dayjs';

interface AppointmentSettings {
  requireCreditCard: boolean;
  // Set to 'usaepay' or 'dejavoo' when the clinic has card capture wired.
  // Absent means no processor is configured (backend will have flipped
  // requireCreditCard to false in that case).
  processor?: 'usaepay' | 'dejavoo';
  // USAePay-only — public key for the client-side PayJS tokenizer.
  epayPublicKey?: string;
  // Dejavoo-only — ecommToken for the Freedom-to-Design SDK. credentialId is
  // what the backend uses to save the tokenized card to DejavooCustomerTokens.
  dejavoo?: {
    ecommToken: string;
    credentialId: number;
  };
}

export interface AvailabilitySlot {
  provider: string;
  date: string;
  startTime: Dayjs;
  endTime: Dayjs;
  startTimeDisplay: string;
  endTimeDisplay: string;
  duration: number;
  tz: string;
}
export interface AvailabilityResponse {
  [date: string]: {
    [providerId: string]: AvailabilitySlot[];
  };
}

type AvailabilityApiResponse = {
  appointmentSettings: AppointmentSettings;
} & {
  [date: string]: {
    [providerId: string]: {
      provider: string;
      date: string;
      startTime: number;
      endTime: number;
      startTimeDisplay: string;
      endTimeDisplay: string;
      duration: number;
      tz: string;
    }[];
  };
};

const locationService = () => {
  const getLocation = async (path: string): Promise<any> => {
    return ChiroUpAPI.get('api-noauth', `/location/${path}`, {});
  };

  const getAvailability = async ({
    path,
    provider,
    year,
    month,
    treatment,
    timezone,
  }: {
    path: string;
    provider: string | null;
    year: string;
    month: string;
    treatment: number | null;
    timezone: string;
  }): Promise<{
    formattedAvailability: AvailabilityResponse;
    appointmentSettings: AppointmentSettings;
  }> => {
    const response = (await ChiroUpAPI.get('api-noauth', `/location/${path}/availability`, {
      queryParams: {
        path,
        provider,
        year,
        month,
        treatment,
      },
    })) as AvailabilityApiResponse;

    const { appointmentSettings, ...availableDates } = response || {};

    const formattedAvailability = Object.entries(availableDates).reduce(
      (acc: AvailabilityResponse, [dateKey, providerMap]) => {
        acc[dateKey] = Object.entries(providerMap).reduce(
          (providerAcc: { [providerId: string]: AvailabilitySlot[] }, [providerId, slots]) => {
            providerAcc[providerId] = slots?.map(slot => ({
              ...slot,
              startTime: createDayjs({ datetime: slot.startTime, timezone }) as Dayjs,
              endTime: createDayjs({ datetime: slot.endTime, timezone }) as Dayjs,
            }));

            return providerAcc;
          },
          {}
        );

        return acc;
      },
      {}
    );

    return {
      formattedAvailability,
      appointmentSettings,
    };
  };

  // Resolve the Dejavoo manual-entry ecommToken for the selected patient's
  // assigned location (with a primary-location fallback). Authenticated — the
  // browser must tokenize with the same merchant the backend will charge.
  // Returns null when no manual device is configured (can't collect a card).
  const getDejavooManualConfig = async ({
    path,
    patientId,
  }: {
    path: string;
    patientId: string;
  }): Promise<{ processor: 'dejavoo'; ecommToken: string; credentialId: number } | null> => {
    return ChiroUpAPI.get('api', `/location/${path}/dejavoo-manual-config/${patientId}`, {});
  };

  const getAppointments = async (path: string): Promise<any> => {
    return ChiroUpAPI.get('api', `/appointment/${path}`, {});
  };

  const cancelAppointment = async (appointmentId: string, reason: string): Promise<void> => {
    return ChiroUpAPI.del('api', `/appointment/${appointmentId}/${reason}`, {});
  };

  return {
    getLocation,
    getAvailability,
    getDejavooManualConfig,
    getAppointments,
    cancelAppointment,
  };
};

export default locationService();
